import { Duration } from 'dayjs/plugin/duration';

import { SBaseDisplay, SDuration } from '@summit/core';
import { MileageFormatter, SummitFormattedText } from '@summit/models';

import { EboObjectTO } from '../base-classes/ebo-object-to';
import { MaintItem } from './maint-item';
import { MaintPlan } from './maint-plan';

export class MaintPlanItem extends EboObjectTO implements SBaseDisplay {
  planId: string;
  plan: MaintPlan;
  itemId: string;
  item: MaintItem;
  mileage: number;
  mileageThresholdFlag: number;
  mileageThresholdWarn: number;
  mileageThresholdAlarm: number;
  duration: Duration;
  durationThresholdFlag: Duration;
  durationThresholdWarn: Duration;
  durationThresholdAlarm: Duration;

  get durationInDays(): number | undefined {
    if (!this.duration) return undefined;
    return this.duration.asDays();
  }
  set durationInDays(value: number | undefined) {
    if (!value) this.duration = undefined;
    else this.duration = SDuration.new({ day: value });
  }

  get durationThresholdFlagInDays(): number | undefined {
    if (!this.durationThresholdFlag) return undefined;
    return this.durationThresholdFlag.asDays();
  }
  set durationThresholdFlagInDays(value: number | undefined) {
    if (!value) this.durationThresholdFlag = null;
    else this.durationThresholdFlag = SDuration.new({ day: value });
  }
  get durationThresholdWarnInDays(): number | undefined {
    if (!this.durationThresholdWarn) return undefined;
    return this.durationThresholdWarn.asDays();
  }
  set durationThresholdWarnInDays(value: number | undefined) {
    if (!value) this.durationThresholdWarn = undefined;
    else this.durationThresholdWarn = SDuration.new({ day: value });
  }

  get durationThresholdAlarmInDays(): number | undefined {
    if (!this.durationThresholdAlarm) return undefined;
    return this.durationThresholdAlarm.asDays();
  }
  set durationThresholdAlarmInDays(value: number | undefined) {
    if (!value) this.durationThresholdAlarm = undefined;
    else this.durationThresholdAlarm = SDuration.new({ day: value });
  }

  get mileageThresholds(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.mileageThresholdFlag) response.AddKeyValues(`Flag`, `${MileageFormatter(this.mileageThresholdFlag)} Mi.`);
    if (this.mileageThresholdWarn) response.AddKeyValues(`Warn`, `${MileageFormatter(this.mileageThresholdWarn)} Mi.`);
    if (this.mileageThresholdAlarm)
      response.AddKeyValues(`Alarm`, `${MileageFormatter(this.mileageThresholdAlarm)} Mi.`);
    return response;
  }

  get durationThresholds(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.durationThresholdFlag?.asDays() > 0)
      response.AddKeyValues('Flag', `${this.durationThresholdFlag.asDays()} Days`);
    if (this.durationThresholdWarn?.asDays() > 0)
      response.AddKeyValues(`Warn`, `${this.durationThresholdWarn.asDays()} Days`);
    if (this.durationThresholdAlarm?.asDays() > 0)
      response.AddKeyValues(`Alarm`, `${this.durationThresholdAlarm.asDays()} Days`);
    return response;
  }

  constructor(jsonData?: Partial<MaintPlanItem>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.plan) this.plan = new MaintPlan(jsonData.plan);
      if (jsonData.item) this.item = new MaintItem(jsonData.item);
      if (jsonData.duration) this.duration = SDuration.new(jsonData.duration);
      if (jsonData.durationThresholdFlag) this.durationThresholdFlag = SDuration.new(jsonData.durationThresholdFlag);
      if (jsonData.durationThresholdWarn) this.durationThresholdWarn = SDuration.new(jsonData.durationThresholdWarn);
      if (jsonData.durationThresholdAlarm) this.durationThresholdAlarm = SDuration.new(jsonData.durationThresholdAlarm);
    }
  }

  toShortString(): string {
    const response = [this.item?.name, 'every'];
    if (this.mileage) response.push(`${MileageFormatter(this.mileage)} Mi.`);
    if (this.mileage && this.duration?.asDays() > 0) response.push('or');
    if (this.duration?.asDays() > 0) response.push(`${this.duration.asDays()} Days`);
    return response.join(' ');
  }
}
